
import {
  Component,
  Emit,
  namespace,
  Prop,
  Vue,
  Watch,
} from "nuxt-property-decorator";
import { debounce } from "~/operations/debounce";
import {
  ButtonText,
  DeliveryTimes,
  DeliveryTruck,
  HoseLengths,
  InputFieldLabel,
  InputPlaceholder,
  LabelAdditives,
  PaymentTypes,
  PelletQualities,
} from "~/operations/messages";
import { breakpoints } from "~/operations/screensizes";
import {
  calculatePelletsAmount,
  getSorteConfig,
  PELLETS_AMOUNT_LIMITS,
} from "~/utils/pelletsSorte";

const CalculatorModule = namespace("calculator");

const STORAGE_KEY = "pellets-filter-params";

const COMPARISON_FIELDS = [
  "zipcode",
  "amount",
  "unloading_points",
  "payment_type",
  "prod",
  "short_vehicle",
  "hose",
  "deliveryTimes",
];

@Component({
  components: {
    DeliveryTypeRadio: () =>
      import("~/components/molecules/display/DeliveryTypeRadio.vue"),
  },
})
export default class PelletsPriceComparison extends Vue {
  @Prop({ default: "Pelletspreise" }) title: string;
  @Prop({ default: "" }) cityData: string;
  @Prop() selectPelletsOption: string;

  @CalculatorModule.State("currentTime")
  currentTime: any;

  @CalculatorModule.Action("fetchAvailableOptions")
  _fetchAvailableOptions;

  @CalculatorModule.State("availabilities")
  availabilities;

  ButtonText = ButtonText;
  InputFieldLabel = InputFieldLabel;
  InputPlaceholder = InputPlaceholder;
  LabelAdditives = LabelAdditives;
  PelletQualities = PelletQualities;
  HoseLengths = HoseLengths;
  PaymentTypes = PaymentTypes;
  DeliveryTruck = DeliveryTruck;

  showMoreOptions = false;
  showHeader = true;
  userZipCodeSupportMessage = "";
  userOilAmountSupportMessage = "";

  requestData: any = this.getInitialRequestData();
  requestDataComparsion: any = this.getInitialRequestData();

  get unloading_pointsOptions(): Array<{ name: string; value: number }> {
    return Array.from({ length: 9 }, (_, i) => ({
      name: `${i + 1}`,
      value: i + 1,
    }));
  }

  hoseLengthOptions: Array<{ name: string; value: string }> = [
    { name: `${DeliveryTruck.hoseLength1}`, value: `fortyMetre` },
    { name: `${DeliveryTruck.hoseLength2}`, value: `sixtyMetre` },
    { name: `${DeliveryTruck.hoseLength3}`, value: `eightyMetre` },
  ];

  vehicleTrailerPelletsOptions: Array<{ name: string; value: string }> = [
    { name: `${DeliveryTruck.withTrailer}`, value: `withTrailer` },
    { name: `${DeliveryTruck.withoutTrailer}`, value: `withoutTrailer` },
    { name: `${DeliveryTruck.smallTruck}`, value: "smallTruck" },
  ];

  productOptions: Array<{ name: string; value: string }> = [];

  selectPaymentOptions: Array<{ name: string; value: string }> = [
    { name: PaymentTypes.ec, value: `ec` },
    { name: PaymentTypes.cash, value: `cash` },
    { name: PaymentTypes.guaranteedPayment, value: `guaranteedPayment` },
    { name: PaymentTypes.invoice, value: `invoice` },
    { name: PaymentTypes.directDebit, value: `directDebit` },
    { name: PaymentTypes.inAdvance, value: `inAdvance` },
    { name: PaymentTypes.all, value: `all` },
  ];

  selectDeliveryDateOptions = DeliveryTimes;

  loadAvailabilityOptions = true;
  nextButtonAnimated = false;

  get isLargeViewport(): boolean {
    if (typeof window === "undefined") return false;
    return window.innerWidth > breakpoints.lg;
  }

  async created() {
    if (this.requestData.zipcode && !process.server) {
      await this.fetchAvailableOptions();
    }
  }

  mounted() {
    const isCityPage = this.$route.path.includes("-");

    if (isCityPage) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          type: "city",
          path: this.$route.path,
        }),
      );
    } else if (Object.keys(this.$route.query).length > 0) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          type: "bestellung",
          query: this.$route.query,
        }),
      );
    }
  }

  @Watch("$store.state.federalstates.cityRequestData.zipcode")
  getZipcode() {
    const zipcode =
      this.$route.query.zipcode ??
      this.$store.state.federalstates.cityRequestData.zipcode;
    this.requestData.zipcode = zipcode;
    this.requestDataComparsion.zipcode = zipcode;

    if (this.loadAvailabilityOptions) {
      this.fetchAvailableOptions();
    }
    this.loadAvailabilityOptions = false;
  }

  @Watch("requestData", { deep: true })
  onRequestDataChange() {
    this.emitSyncedDataChange();
  }

  @Watch("selectPelletsOption")
  async onPelletsOptionChange(newValue: string) {
    this.requestData.amount = getSorteConfig(newValue).defaultAmount;
    this.userOilAmountSupportMessage = "";
    this.animateCalcButton();
    await this.fetchAvailableOptions({ pelletsDeliveryFormat: newValue });
  }

  dropInvalidDeliveryTimes(availableDeliverTimesOptions: Array<string>) {
    const newList = DeliveryTimes.filter((ele) =>
      availableDeliverTimesOptions.includes(ele.value),
    );
    this.selectDeliveryDateOptions = this.prepareRequestParamsAndSelects(
      availableDeliverTimesOptions,
      "deliveryTimes",
      newList,
      DeliveryTimes,
    );
  }

  dropInvalidPaymentOptions(availablePaymentOptions: Array<string>) {
    const newList = Object.keys(PaymentTypes)
      .filter((item) => availablePaymentOptions.includes(item))
      .map((item) => ({ name: PaymentTypes[item], value: item }));

    this.selectPaymentOptions = this.prepareRequestParamsAndSelects(
      availablePaymentOptions,
      "payment_type",
      newList,
      PaymentTypes,
    );
  }

  get debouncedWatchZipCodeInput() {
    return debounce(this.watchZipCodeInput, 300);
  }

  get debouncedWatchAmountInput() {
    return debounce(this.watchAmountInput, 300);
  }

  get sorte() {
    return getSorteConfig(this.selectPelletsOption);
  }

  get placeHolderForAmount(): number {
    return this.selectPelletsOption === "pallet" ? 1 : InputPlaceholder.amount;
  }

  get showAmountInput(): boolean {
    return this.selectPelletsOption === "loose";
  }

  get amountLabel() {
    return this.sorte.label;
  }

  get amountLabelShort() {
    return this.sorte.labelShort;
  }

  get amountLabelHasPrefix(): boolean {
    return this.sorte.hasPrefix;
  }

  get amountIcon(): string {
    return this.sorte.icon;
  }

  dropInvalidProductOptions(availableProductOptions: Array<string>) {
    const products = PelletQualities;
    const newList = Object.keys(products)
      .filter((item) => availableProductOptions.includes(item))
      .map((item) => ({ name: products[item], value: item }));

    // API does not yet return "Alle Qualitäten" so prepend it manually
    newList.unshift({
      name: "Alle Qualitäten",
      value: "isoNorm, enPlus, dinPlus",
    });

    this.productOptions = this.prepareRequestParamsAndSelects(
      availableProductOptions,
      "prod",
      newList,
      products,
    );
  }

  dropInvalidHoseLengthstOptions(availableProductOptions: Array<string>) {
    const products = this.HoseLengths;
    const newList = Object.keys(products)
      .filter((item) => availableProductOptions.includes(item))
      .map((item) => ({ name: products[item], value: item }));

    this.hoseLengthOptions = this.prepareRequestParamsAndSelects(
      availableProductOptions,
      "hose",
      newList,
      products,
    );
  }

  /**
   * Damit im FireFox die Darstellung der Select-Box mit dem Wert für den
   * Preisrechner (aus der URL) übereinstimmt. Muss bei einem PLZ wechsel
   * die neue Zahlart (wenn bei der neuen PLZ die vorher gewünschte Zahlart nicht
   * angeboten wird) in der URL eingetragen werden oder die ausgewählte Zahlart
   * an die erste Stelle der Select-Box geschoben werden.
   */
  prepareRequestParamsAndSelects(
    availableOptions,
    requestDataOption,
    newList,
    origin,
  ) {
    if (
      availableOptions.includes(String(this.requestData[requestDataOption]))
    ) {
      newList.unshift({
        name: origin[String(this.requestData[requestDataOption])],
        value: this.requestData[requestDataOption],
      });
    } else {
      this.requestData[requestDataOption] = newList[0].value;
    }

    return [...new Map(newList.map((ele) => [ele.value, ele])).values()] as any;
  }

  async fetchAvailableOptions(options?: { pelletsDeliveryFormat?: string }) {
    if (!this.requestData.zipcode || !this.requestData.amount) return;

    const routeProd = this.$route.query.prod;
    let amount: number;
    if (options?.pelletsDeliveryFormat) {
      amount = calculatePelletsAmount(
        this.requestData.amount,
        options.pelletsDeliveryFormat,
      );
    } else if (routeProd && routeProd !== "loose") {
      amount = this.requestData.amount * 1000;
    } else {
      amount = this.requestData.amount;
    }

    try {
      await this._fetchAvailableOptions({
        isPellets: true,
        zipcode: this.requestData.zipcode,
        type: options?.pelletsDeliveryFormat || routeProd,
        amount,
      });

      this.handleAvailabilities();
    } catch (error) {
      console.error(error);
    }
  }

  async watchZipCodeInput(value: string) {
    this.requestData.zipcode = value;

    requestAnimationFrame(async () => {
      const zipcode = this.requestData.zipcode;
      if (zipcode.length > 5) {
        this.userZipCodeSupportMessage = "Postleitzahl ist zu lang";
      } else {
        this.userZipCodeSupportMessage = "";
      }

      if (
        zipcode.length === 5 &&
        zipcode !== this.requestDataComparsion.zipcode
      ) {
        await this.fetchAvailableOptions({
          pelletsDeliveryFormat: this.selectPelletsOption,
        });
      }
    });
  }

  async watchAmountInput(value: string) {
    this.requestData.amount = parseInt(value);

    const limit =
      this.selectPelletsOption === "loose"
        ? PELLETS_AMOUNT_LIMITS.loose
        : PELLETS_AMOUNT_LIMITS.packed;

    requestAnimationFrame(() => {
      if (this.requestData.amount > limit.max) {
        this.userOilAmountSupportMessage =
          `Maximum ${limit.max.toLocaleString()} ${limit.unit}`.trim();
      } else if (this.requestData.amount < limit.min) {
        this.userOilAmountSupportMessage =
          `Minimum ${limit.min.toLocaleString()} ${limit.unit}`.trim();
      } else {
        this.userOilAmountSupportMessage = "";
      }
    });
  }

  handleAvailabilities() {
    this.dropInvalidProductOptions(
      this.availabilities.qualitySteps ?? ["normal"],
    );
    this.dropInvalidDeliveryTimes(
      this.availabilities.deliveryTimes ?? ["normal"],
    );
    this.dropInvalidPaymentOptions(this.availabilities.paymentTypes ?? ["all"]);
    this.dropInvalidHoseLengthstOptions(
      this.availabilities.hoseLengths ?? ["thirtyMetre"],
    );
  }

  getInitialRequestData() {
    const query = this.$route.query;
    const amount = query.amount;
    let initialAmount: number;
    if (!amount) {
      initialAmount = 6000;
    } else if (query.prod !== "loose") {
      initialAmount = Number(amount) / 1000;
    } else {
      initialAmount = Number(amount);
    }

    return {
      zipcode:
        query.zipcode ??
        this.$store.state.federalstates.cityRequestData.zipcode,
      unloading_points: Number(query.unloading_points ?? 1),
      payment_type: query.payment_type ?? "all",
      hose: query.hose ?? "thirtyMetre",
      short_vehicle: query.short_vehicle ?? "withTrailer",
      deliveryTimes: query.deliveryTimes ?? "normal",
      prod: query.quality_steps ?? "isoNorm, enPlus, dinPlus",
      amount: initialAmount,
    };
  }

  toggleMoreOptions() {
    this.showMoreOptions = !this.showMoreOptions;
  }

  @Emit()
  updatePelletsValue(value: string): string {
    return value;
  }

  animateCalcButton() {
    this.nextButtonAnimated = true;
    setTimeout(() => {
      this.nextButtonAnimated = false;
    }, 1000);
  }

  async fetchCityDate(zipcode) {
    await this.$store.dispatch("federalstates/fetchCityName", {
      zipcode,
      basePath: "pelletspreise",
    });
  }

  async validateAmount(amount: number) {
    const limit =
      this.selectPelletsOption === "loose"
        ? PELLETS_AMOUNT_LIMITS.loose
        : PELLETS_AMOUNT_LIMITS.packed;

    if (amount > limit.max) {
      this.userOilAmountSupportMessage =
        `Maximum ${limit.max.toLocaleString()} ${limit.unit}`.trim();
      return true;
    }
    if (amount < limit.min) {
      this.userOilAmountSupportMessage =
        `Minimum ${limit.min.toLocaleString()} ${limit.unit}`.trim();
      return true;
    }
    return false;
  }

  async validateZipCode(zipcode: string) {
    if (zipcode.length === 5) return false;
    this.userZipCodeSupportMessage =
      zipcode.length > 5
        ? "Postleitzahl ist zu lang"
        : "Postleitzahl ist zu kurz";
    return true;
  }

  async validateAndStoreUserInput() {
    const amountInvalid = await this.validateAmount(this.requestData.amount);
    const zipInvalid = amountInvalid
      ? true
      : await this.validateZipCode(this.requestData.zipcode);

    if (!amountInvalid && !zipInvalid) {
      await this.emitAndStoreFormData();
    }
    await this.fetchCityDate(this.requestData.zipcode);
  }

  calculateAmount(): string {
    return String(
      calculatePelletsAmount(this.requestData.amount, this.selectPelletsOption),
    );
  }

  createQueryParams() {
    return {
      zipcode: this.requestData.zipcode,
      amount: this.calculateAmount(),
      unloading_points: this.requestData.unloading_points.toString(),
      payment_type: this.requestData.payment_type,
      prod: this.selectPelletsOption,
      quality_steps: this.requestData.prod,
      hose: this.requestData.hose,
      short_vehicle: this.requestData.short_vehicle,
      deliveryTimes: this.requestData.deliveryTimes,
      type: this.selectPelletsOption,
    };
  }

  @Emit()
  async emitAndStoreFormData() {
    this.equalizeDataComparisonObject();

    const queryParams = this.createQueryParams();

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        type: "bestellung",
        query: queryParams,
      }),
    );

    await this.$router.push({ query: queryParams });
    this.emitSyncedDataChange();
    return this.requestData;
  }

  equalizeDataComparisonObject() {
    for (const field of COMPARISON_FIELDS) {
      this.requestDataComparsion[field] = this.requestData[field];
    }
  }

  @Emit()
  emitSyncedDataChange() {
    this.animateCalcButton();

    for (const field of COMPARISON_FIELDS) {
      if (this.requestDataComparsion[field] !== this.requestData[field]) {
        return false;
      }
    }

    return true;
  }
}
